import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import type { AuthSession, AuthUser, LoginRequest } from '@mvcp/contracts';
import {
  login as apiLogin,
  logout as apiLogout,
  refreshSession,
  setAccessToken,
  setOnSessionExpired,
} from '../api';

type AuthStatus = 'loading' | 'anonymous' | 'authenticated';

/** setTimeout clamps above this; keep the renew delay within range. */
const MAX_TIMEOUT_MS = 2_147_483_647;

interface AuthContextValue {
  status: AuthStatus;
  user: AuthUser | null;
  /** Sign in; throws with the (generic) server error message on failure. */
  signIn: (credentials: LoginRequest) => Promise<void>;
  signOut: () => Promise<void>;
  /** Adopt a session obtained out-of-band (e.g. invite activation). */
  adoptSession: (session: AuthSession) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Renew slightly before expiry so an active session never bounces to Login. */
const RENEW_SKEW_SECONDS = 30;

export function AuthProvider({
  children,
  /** Test seam: seed an authenticated session and skip the bootstrap refresh. */
  initialSession,
}: {
  children: React.ReactNode;
  initialSession?: AuthSession;
}): React.JSX.Element {
  const [status, setStatus] = useState<AuthStatus>(
    initialSession ? 'authenticated' : 'loading',
  );
  const [user, setUser] = useState<AuthUser | null>(
    initialSession?.user ?? null,
  );
  const renewTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  const clearTimer = useCallback(() => {
    if (renewTimer.current) {
      clearTimeout(renewTimer.current);
      renewTimer.current = undefined;
    }
  }, []);

  // Forward-declared so scheduleRenew and adoptSession can reference each other.
  const scheduleRenewRef = useRef<(expiresInSeconds: number) => void>(() => {});

  const adoptSession = useCallback(
    (session: AuthSession) => {
      setAccessToken(session.accessToken);
      setUser(session.user);
      setStatus('authenticated');
      scheduleRenewRef.current(session.expiresInSeconds);
    },
    [],
  );

  const goAnonymous = useCallback(() => {
    clearTimer();
    setAccessToken(null);
    setUser(null);
    setStatus('anonymous');
  }, [clearTimer]);

  const scheduleRenew = useCallback(
    (expiresInSeconds: number) => {
      clearTimer();
      // Guard a NaN/absent/huge server value: an unclamped delay overflows the
      // setTimeout ceiling and fires immediately → a tight refresh loop.
      const seconds = Number.isFinite(expiresInSeconds) ? expiresInSeconds : 0;
      const delayMs = Math.min(
        Math.max(seconds - RENEW_SKEW_SECONDS, 1) * 1000,
        MAX_TIMEOUT_MS,
      );
      renewTimer.current = setTimeout(() => {
        refreshSession()
          .then((session) => adoptSession(session))
          .catch(() => goAnonymous());
      }, delayMs);
    },
    [adoptSession, clearTimer, goAnonymous],
  );
  scheduleRenewRef.current = scheduleRenew;

  // Bootstrap: if we were seeded, schedule renew; otherwise try to restore a
  // session from the httpOnly refresh cookie (survives a page reload).
  useEffect(() => {
    let cancelled = false;
    if (initialSession) {
      scheduleRenew(initialSession.expiresInSeconds);
      return () => {
        cancelled = true;
        clearTimer();
      };
    }
    refreshSession()
      .then((session) => {
        if (!cancelled) adoptSession(session);
      })
      .catch(() => {
        if (!cancelled) setStatus('anonymous');
      });
    return () => {
      cancelled = true;
      clearTimer();
    };
    // Bootstrap runs exactly once on mount; helpers are stable refs/callbacks.
  }, []);

  const signIn = useCallback(
    async (credentials: LoginRequest) => {
      const session = await apiLogin(credentials);
      adoptSession(session);
    },
    [adoptSession],
  );

  const signOut = useCallback(async () => {
    // Cancel the renew timer FIRST: if it fired during the logout await it
    // could re-adopt a session the user just asked to end.
    clearTimer();
    try {
      await apiLogout();
    } finally {
      goAnonymous();
    }
  }, [clearTimer, goAnonymous]);

  // Let a failed reactive refresh (in authFetch) return the user to Login.
  useEffect(() => {
    setOnSessionExpired(() => goAnonymous());
    return () => setOnSessionExpired(null);
  }, [goAnonymous]);

  return (
    <AuthContext.Provider value={{ status, user, signIn, signOut, adoptSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
